import Konva from 'konva';
import useCanvas from 'components/canvas/hooks/use-canvas';
import { useCanvasStageOperations } from 'state/canvas/stage';
import { isMobile } from 'react-device-detect';

interface UseCanvasZoom {
  zoomIn(): void;
  zoomOut(): void;
  zoomTo100(): void;
  zoomToFitAll(padding?: number): void;
  zoomToPercentage(): void;
  handleWheelZoom(event: any): void;
  handlePinchZoom(event: any): void;
}

const scaleBy = 0.01;
const minScale = 0.01;
const maxScale = 3;

export default function useCanvasZoom(surfaceId: string): UseCanvasZoom {
  const { getCanvasStage, updateCanvasStage } = useCanvasStageOperations();

  const {
    stageInstance,
    findMostLeftPoint,
    findMostRightPoint,
    findHighestPoint,
    findMostLowestPoint,
  } = useCanvas();

  function zoomIn() {
    const stage = getCanvasStage();
    const newScale = stage.scale + stage.scaleBy;

    handleZoom(newScale);
  }

  function zoomOut() {
    const stage = getCanvasStage();
    const newScale = stage.scale - stage.scaleBy;

    handleZoom(newScale);
  }

  function zoomTo100(): void {
    if (stageInstance) {
      stageInstance.scaleX(1.0);
      stageInstance.scaleY(1.0);

      updateCanvasStage({
        scale: 1.0,
      });
    }
  }

  function zoomToPercentage() {
    const stage = getCanvasStage();
    return Math.round(parseFloat(stage.scale?.toFixed(2) ?? 1) * 100) + '%';
  }

  function zoomToFitAll() {
    const padding = 100;

    if (stageInstance) {
      const layerX = findMostLeftPoint() + padding;

      const layerY = findHighestPoint() + padding;

      const layerWidth = Math.abs(findMostRightPoint() - findMostLeftPoint());

      const layerHeight = Math.abs(findMostLowestPoint() - findHighestPoint());

      const scaleX = stageInstance?.width() / layerWidth;
      const scaleY = stageInstance?.height() / layerHeight;

      const newScaleX = getNewScale(scaleX);
      const newScaleY = getNewScale(scaleY);

      const newScale = Math.min(newScaleX, newScaleY) * 0.9;

      const newX = -layerX * newScale + padding;
      const newY = -layerY * newScale + padding;

      stageInstance.scaleX(newScale);
      stageInstance.scaleY(newScale);

      stageInstance.x(newX);
      // @ts-ignore
      stageInstance.y(newY);

      updateCanvasStage({
        x: newX,
        y: newY,
        scale: newScale,
      });
    }
  }

  function getNewScale(scale: number): number {
    if (scale < 0) {
      return minScale;
    }

    if (scale > maxScale) {
      return maxScale;
    }

    return scale;
  }

  function handleZoom(newScale: number) {
    const stage = getCanvasStage();
    const oldScale = stage.scale;

    if (stageInstance) {
      const center = {
        x: stageInstance?.width() / 2,
        y: stageInstance?.height() / 2,
      };

      const relatedTo = {
        x: (center.x - stageInstance?.x()) / oldScale,
        y: (center.y - stageInstance?.y()) / oldScale,
      };

      const newPosition = {
        x: center.x - relatedTo.x * newScale,
        y: center.y - relatedTo.y * newScale,
      };

      if (newScale > 0 && newScale < maxScale) {
        updateCanvasStage({
          scale: newScale,
          x: newPosition.x,
          y: newPosition.y,
        });
      }
    }
  }

  function handleWheelZoom(event) {
    if (isMobile) {
      return;
    }

    const deltaY = event?.evt?.deltaY ?? event.deltaY;

    if (stageInstance) {
      const pointerPosition = stageInstance.getPointerPosition() as Konva.Vector2d;
      const oldScale = stageInstance?.scaleX();
      const newScale = deltaY < 0 ? oldScale + scaleBy : oldScale - scaleBy;

      if (newScale > 0 && newScale < maxScale) {
        const mousePointTo = {
          x: pointerPosition.x / oldScale - stageInstance.x() / oldScale,
          y: pointerPosition.y / oldScale - stageInstance.y() / oldScale,
        };

        updateCanvasStage({
          scale: newScale,
          x: -(mousePointTo.x - pointerPosition.x / newScale) * newScale,
          y: -(mousePointTo.y - pointerPosition.y / newScale) * newScale,
        });
      }
    }
  }

  function handlePinchZoom(event) {
    if (stageInstance) {
      const oldScale = stageInstance?.scaleX();

      const newScale =
        event.deltaY < 0 ? oldScale + scaleBy : oldScale - scaleBy;

      if (newScale > 0 && newScale < maxScale) {
        updateCanvasStage({ scale: newScale });
      }
    }
  }

  return {
    zoomIn,
    zoomOut,
    zoomTo100,
    zoomToFitAll,
    handlePinchZoom,
    handleWheelZoom,
    zoomToPercentage,
  };
}
